<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmRemoveProductDialog"
            @on-confirm="removeProduct"
            @on-abort="closeConfirmRemoveProductDialog"
        >
            <template #title>
                Usuwanie produktu
            </template>
            <template #content>
                Czy na pewno chcesz usunąć produkt?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <OrderSendingParametersDialog
            :is-open="sendingParametersDialog"
            :order-delivery-method="order.orderDeliveryMethod"
            :delivery-methods="deliveryMethods"
            @on-close="closeSendingParametersDialog"
            @on-save="saveSendingParametersDialog"
        />

        <AdviceNoteDialog
            :is-open="adviceNoteDialog"
            :advice-note="adviceNoteDraft"
            defer-save
            @on-close="closeAdviceNoteDialog"
            @on-save="handleAdviceNoteSaved"
        />

        <page-title>
            {{ pageTitle }}
        </page-title>

        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row>
                                        <v-col cols="12" md="3">
                                            <v-checkbox
                                                v-model="order.linkedToCampaign"
                                                class="mt-0"
                                                label="Zamówienie kampanijne"
                                                hide-details
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <v-select
                                                v-model="order.numberConfigurationTypeId"
                                                :items="numberConfigurationTypeNames"
                                                label="Numeracja zamówienia"
                                                item-value="id"
                                                item-text="name"
                                                outlined
                                                dense
                                                :error-messages="orderNumberConfigurationTypeIdErrors"
                                                required
                                                @change="temporarySaveOrderData"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                v-model="order.name"
                                                label="Nazwa zamówienia klienta"
                                                outlined
                                                :error-messages="orderNameErrors"
                                                dense
                                                :rules="validRuleOrderName"
                                                @change="temporarySaveOrderData"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-autocomplete
                                                v-model="order.customer"
                                                label="Klient"
                                                :items="customersFound"
                                                :filter="customFilter"
                                                :loading="customerSearchLoading"
                                                :search-input.sync="customerSearchQuery"
                                                class="v-text-field--placeholder-dark"
                                                item-text="name"
                                                return-object
                                                outlined
                                                dense
                                                :error-messages="orderCustomerIdErrors"
                                                required
                                                no-data-text="Nie znaleziono"
                                                @change="changeCustomer"
                                            >
                                                <template slot="selection" slot-scope="data">
                                                    {{ data.item.name }}
                                                </template>

                                                <template slot="item" slot-scope="data">
                                                    {{ data.item.name }}
                                                </template>

                                                <template slot="no-data">
                                                    <v-list-item>
                                                        <v-list-item-title>
                                                            <template v-if="!customerSearchQuery">
                                                                Wpisz treść aby odnaleźć <strong>klienta</strong>.
                                                            </template>
                                                            <template v-else-if="customerSearchQuery.length < customerSearchQueryMinLength">
                                                                Wpisz przynajmniej <strong>{{ customerSearchQueryMinLength }}</strong> znaki.
                                                            </template>
                                                            <template v-else-if="customerSearchLoading">
                                                                Trwa wyszukiwanie klienta...
                                                            </template>
                                                            <template v-else>
                                                                Nie odnaleziono klienta na zapytanie: "<strong> {{ customerSearchQuery }}".</strong>
                                                            </template>
                                                        </v-list-item-title>
                                                    </v-list-item>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-autocomplete
                                                v-model="order.ownerId"
                                                :items="users"
                                                label="Właściciel zamówienia"
                                                item-value="id"
                                                no-data-text="Nie znaleziono"
                                                outlined
                                                dense
                                                :filter="customUsersFilter"
                                            >
                                                <template slot="selection" slot-scope="data">
                                                    <template v-if="data.item.firstName && data.item.lastName">
                                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                                    </template>
                                                    <template v-else>
                                                        {{ data.item.username }}
                                                    </template>
                                                </template>
                                                <template slot="item" slot-scope="data">
                                                    <template v-if="data.item.firstName && data.item.lastName">
                                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                                    </template>
                                                    <template v-else>
                                                        {{ data.item.username }}
                                                    </template>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" md="6" sm="6">
                                            <v-text-field
                                                v-model.lazy="order.netPrice"
                                                v-money="moneySettings"
                                                label="Cena netto sprzedaży"
                                                outlined
                                                dense
                                                @change="temporarySaveOrderData"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6" sm="6">
                                            <v-text-field
                                                v-model.lazy="order.valuation"
                                                v-money="moneySettings"
                                                label="Koszt materiałów"
                                                outlined
                                                dense
                                                @change="temporarySaveOrderData"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-datetime-picker
                                                v-model="plannedSendDate"
                                                label="Planowana data wysyłki"
                                                date-format="dd.MM.yyyy"
                                                :date-picker-props="datetimeProps.dateProps"
                                                :text-field-props="datetimeProps.textFieldProps"
                                                :time-picker-props="datetimeProps.timeProps"
                                                @input="plannedSendDateHandler"
                                            >
                                                <template slot="dateIcon">
                                                    <v-icon>mdi-calendar</v-icon>
                                                </template>
                                                <template slot="timeIcon">
                                                    <v-icon>mdi-clock-outline</v-icon>
                                                </template>
                                                <template slot="actions" slot-scope="{parent}">
                                                    <v-btn text color="error" @click.native="parent.clearHandler">
                                                        Wyczyść
                                                    </v-btn>
                                                    <v-btn text color="success" @click="parent.okHandler">
                                                        Zapisz
                                                    </v-btn>
                                                </template>
                                            </v-datetime-picker>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-datetime-picker
                                                v-model="plannedDeliveryDate"
                                                label="Planowana data dostawy"
                                                date-format="dd.MM.yyyy"
                                                :date-picker-props="datetimeProps.dateProps"
                                                :text-field-props="datetimeProps.textFieldProps"
                                                :time-picker-props="datetimeProps.timeProps"
                                                @input="plannedDeliveryDateHandler"
                                            >
                                                <template slot="dateIcon">
                                                    <v-icon>mdi-calendar</v-icon>
                                                </template>
                                                <template slot="timeIcon">
                                                    <v-icon>mdi-clock-outline</v-icon>
                                                </template>
                                                <template slot="actions" slot-scope="{parent}">
                                                    <v-btn text color="error" @click.native="parent.clearHandler">
                                                        Wyczyść
                                                    </v-btn>
                                                    <v-btn text color="success" @click="parent.okHandler">
                                                        Zapisz
                                                    </v-btn>
                                                </template>
                                            </v-datetime-picker>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-menu
                                                v-model="receiveMaterialDatepicker"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                bottom
                                                nudge-bottom="40"
                                                nudge-right="34"
                                                min-width="290px"
                                            >
                                                <template #activator="{on}">
                                                    <v-text-field
                                                        :value="formatDate(order.receiveMaterialDate, false, false)"
                                                        label="Data dostawy surowca"
                                                        prepend-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        :clearable="!!order.receiveMaterialDate"
                                                        v-on="on"
                                                        @click:clear="clearReceiveMaterialDate"
                                                        @input="temporarySaveOrderData"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    :value="receiveMaterialDate"
                                                    no-title
                                                    scrollable
                                                    first-day-of-week="1"
                                                    @input="receiveMaterialDateHandler"
                                                />
                                            </v-menu>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-menu
                                                v-model="receiveFileDatepicker"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                bottom
                                                nudge-bottom="40"
                                                nudge-right="34"
                                                min-width="290px"
                                            >
                                                <template #activator="{on}">
                                                    <v-text-field
                                                        :value="formatDate(order.receiveFileDate, false, false)"
                                                        label="Data dostarczenia plików"
                                                        prepend-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        :clearable="!!order.receiveFileDate"
                                                        v-on="on"
                                                        @click:clear="clearReceiveFileDate"
                                                        @input="temporarySaveOrderData"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    :value="receiveFileDate"
                                                    no-title
                                                    scrollable
                                                    first-day-of-week="1"
                                                    @input="receiveFileDateHandler"
                                                />
                                            </v-menu>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-menu
                                                v-model="acceptFileDatepicker"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                bottom
                                                nudge-bottom="40"
                                                nudge-right="34"
                                                min-width="290px"
                                            >
                                                <template #activator="{on}">
                                                    <v-text-field
                                                        :value="formatDate(order.acceptFileDate, false, false)"
                                                        label="Data akceptacji plików"
                                                        prepend-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        :clearable="!!order.acceptFileDate"
                                                        v-on="on"
                                                        @click:clear="clearAcceptFileDate"
                                                        @input="temporarySaveOrderData"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    :value="acceptFileDate"
                                                    no-title
                                                    scrollable
                                                    first-day-of-week="1"
                                                    @input="acceptFileDateHandler"
                                                />
                                            </v-menu>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-select
                                                v-model="order.orderGraphicFileDeliveryMethodId"
                                                :items="orderGraphicFileDeliveryMethods"
                                                item-text="name"
                                                item-value="id"
                                                label="Metoda dostarczenia plików graficznych"
                                                :error-messages="orderGraphicFileDeliveryMethodErrors"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="5">
                                            <v-text-field
                                                :value="getDeliveryMethodName(order.orderDeliveryMethod.methodId)"
                                                label="Transport"
                                                prepend-icon="mdi-truck"
                                                outlined
                                                dense
                                                readonly
                                                :error="!$v.order.orderDeliveryMethod.methodId.isValid && $v.order.orderDeliveryMethod.methodId.$dirty"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="2">
                                            <v-text-field
                                                :value="order.orderDeliveryMethod.distance"
                                                label="Dystans"
                                                suffix="km"
                                                outlined
                                                dense
                                                readonly
                                                :error="!$v.order.orderDeliveryMethod.distance.isValid && $v.order.orderDeliveryMethod.distance.$dirty"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="2">
                                            <v-text-field
                                                :value="order.orderDeliveryMethod.cost"
                                                label="Koszt"
                                                suffix="zł"
                                                outlined
                                                dense
                                                readonly
                                                :error="!$v.order.orderDeliveryMethod.cost.isValid && $v.order.orderDeliveryMethod.cost.$dirty"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="1">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            v-on="on"
                                                            @click="showSendingParametersDialog()"
                                                        >
                                                            <v-icon color="blue">
                                                                mdi-pencil
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Edytuj</span>
                                                </v-tooltip>
                                            </div>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-file-input
                                                v-model="deliveryFile"
                                                label="Plan dostaw"
                                                accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                counter
                                                placeholder="Wybierz plik"
                                                prepend-icon="mdi-paperclip"
                                                outlined
                                                dense
                                                :show-size="1000"
                                                @change="temporarySaveOrderData"
                                            >
                                                <template #selection="{index, text}">
                                                    <v-chip
                                                        v-if="index < 2"
                                                        label
                                                        small
                                                    >
                                                        {{ text }}
                                                    </v-chip>
                                                </template>
                                            </v-file-input>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-btn
                                                color="secondary"
                                                data-sentry-label="Awizacja od klienta"
                                                :disabled="actionLoading"
                                                @click="showAdviceNoteDialog"
                                            >
                                                Awizacja od klienta
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                v-model="order.description"
                                                label="Uwagi do zamówienia"
                                                hide-details
                                                outlined
                                                @change="temporarySaveOrderData"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Produkty
                            </h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="secondary"
                                fab
                                small
                                data-sentry-label="Dodaj produkt"
                                class="elevation-1"
                                :disabled="actionLoading"
                                @click="addProduct"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text v-if="hasProducts">
                                    <v-data-table
                                        :headers="headers"
                                        :items="order.orderProducts"
                                        disable-pagination
                                        hide-default-footer
                                        :mobile-breakpoint="0"
                                        item-key="index"
                                    >
                                        <template #item.productName="{item}">
                                            {{ item.name }}
                                        </template>
                                        <template #item.productionFile="{item}">
                                            <div class="d-inline-flex">
                                                <v-icon
                                                    v-if="item.productionFiles.length > 0"
                                                    small
                                                    color="success"
                                                >
                                                    mdi-check
                                                </v-icon>
                                                <v-icon v-else small color="error">
                                                    mdi-close
                                                </v-icon>
                                            </div>
                                        </template>
                                        <template #item.amount="{item}">
                                            {{ Number(item.amount || 0) + Number(item.signalAmount || 0) }}
                                        </template>
                                        <template #item.weight="{item}">
                                            {{ formatWeight(item.weight) }}
                                        </template>
                                        <template #item.numberOfVersions="{item}">
                                            {{ item.numberOfVersions }}
                                        </template>
                                        <template #item.actions="{item}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Edytuj"
                                                            v-on="on"
                                                            @click="editProduct(item.index)"
                                                        >
                                                            <v-icon color="primary">
                                                                mdi-cog
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Konfiguracja produktu</span>
                                                </v-tooltip>
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń"
                                                            v-on="on"
                                                            @click="showConfirmRemoveProductDialog(item.index)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card-text>

                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Nie wybrano produktów do zamówienia.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Komentarze do zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-list v-if="hasComments" two-line>
                                    <template v-for="(comment, index) in order.comments">
                                        <v-list-item
                                            :key="index"
                                        >
                                            <v-list-item-avatar>
                                                <v-avatar
                                                    size="40px"
                                                    color="blue-grey"
                                                >
                                                    <span class="white--text text-uppercase">
                                                        {{ parseUserInitials(comment.username) }}
                                                    </span>
                                                </v-avatar>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ comment.comment }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ comment.username }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider :key="'d-' + index" />
                                    </template>
                                </v-list>
                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak komentarzy do zamówienia.
                                    </v-alert>
                                </v-card-text>

                                <v-card-text>
                                    <v-textarea
                                        v-model="newComment"
                                        label="Dodaj komentarz"
                                        auto-grow
                                        clearable
                                        hide-details
                                        outlined
                                    />
                                    <v-row justify="end">
                                        <v-col cols="auto">
                                            <v-btn
                                                color="primary"
                                                data-sentry-label="Dodaj komentarz"
                                                :disabled="newComment === null || newComment.length <= 0"
                                                @click="addComment"
                                            >
                                                Dodaj
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="error"
                                class="mr-3"
                                data-sentry-label="Anuluj"
                                :disabled="actionLoading"
                                @click="close"
                            >
                                Anuluj
                            </v-btn>
                            <v-btn
                                color="success"
                                data-sentry-label="Utwórz"
                                :disabled="!hasProducts"
                                :loading="actionLoading"
                                @click="saveOrder"
                            >
                                Utwórz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import store from '@/./store'

import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import AdviceNoteDialog from '@/components/common/AdviceNoteDialog'
import OrderSendingParametersDialog from './OrderSendingParametersDialog'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import customFilters from '@/mixins/customFilters'
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'
import priceFormatter from '@/mixins/priceFormatter'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

import { VMoney } from 'v-money'
import { required, helpers } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { validRuleIllegalCharacters, validRuleNameLength } from '@/utils/customValidators'

let customerSearchTimer

const TRADE_DEPARTMENT_ID = 6

export default {
    name: 'OrderCreate',
    components: {
        PageTitle,
        ConfirmDialog,
        AdviceNoteDialog,
        OrderSendingParametersDialog
    },
    directives: { money: VMoney },
    mixins: [
        customFilters,
        dateFormatter,
        parse,
        priceFormatter,
        tooltipDelay,
        weightFormatter
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('orders/getNumberConfigurationTypes'),
            store.dispatch('orders/getOrderGraphicFileDeliveryMethods'),
            store.dispatch('getUser', store.state.user.userId),
            store.dispatch('administration/getUsers', {
                query: `?productionDepartment_id=${TRADE_DEPARTMENT_ID}&isActive=True`
            }),
            store.dispatch('shipments/shipmentsOrders/getDeliveryMethods')
        ])
            .then(responses => {
                next(vm => {
                    vm.pageTitle = to.meta.title
                    vm.numberConfigurationTypes = responses[0].data.results
                    vm.setDefaultNumberConfigurationType()
                    vm.orderGraphicFileDeliveryMethods = responses[1].data.results
                    vm.setDefaultOrderGraphicFileDeliveryMethod()
                    vm.users = responses[3].data.results
                    vm.deliveryMethods = responses[4].data.results
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (!to.params.saveInStore) {
            this.$store.dispatch('orders/clearOrderCreate')
            next()
        } else {
            next()
        }
    },
    data() {
        return {
            pageTitle: '',
            numberConfigurationTypes: [],
            orderGraphicFileDeliveryMethods: [],
            deliveryMethods: [],
            deliveryFile: null,
            order: {
                name: null,
                cooperant: 9, // CFH
                comments: [],
                productNumberOfPackages: 1,
                shopPrefix: 'CF',
                deliveries: [],
                productPropertyValueSet: null,
                numberConfigurationTypeId: null,
                customerId: null,
                ownerId: null,
                grossPrice: 0,
                netPrice: null,
                numberOfTemplates: -1,
                orderProducts: [],
                parameters: [],
                plannedSendDate: null,
                plannedDeliveryDate: null,
                receiveFileDate: null,
                productPackageWeight: 0,
                quantity: 0,
                linkedToCampaign: false,
                shopOrderId: -1,
                shopProductName: 'n/d',
                description: null,
                orderGraphicFileDeliveryMethodId: null,
                orderDeliveryMethod: {
                    methodId: null,
                    cost: null,
                    distance: null
                }
            },
            plannedSendDate: null,
            plannedDeliveryDate: null,
            receiveMaterialDate: null,
            receiveFileDate: null,
            acceptFileDate: null,
            receiveMaterialDatepicker: false,
            receiveFileDatepicker: false,
            acceptFileDatepicker: false,
            datetimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    prependIcon: 'mdi-calendar'
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            productIndex: null,
            customerSearchQuery: null,
            customerSearchQueryMinLength: 3,
            customerSearchLoading: false,
            customersFound: [],
            confirmRemoveProductDialog: false,
            sendingParametersDialog: false,
            adviceNoteDialog: false,
            adviceNoteDraft: null,
            newComment: '',
            actionLoading: false,
            headers: [
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Pliki produkcyjne',
                    value: 'productionFile',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Waga',
                    value: 'weight',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
                {
                    text: 'Ilość wzorów',
                    value: 'numberOfVersions',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            users: [],
            validRuleOrderName: [
                value => validRuleNameLength(value, 100) || 'Nazwa nie może być dłuższa niż 100 znaków',
                value => !validRuleIllegalCharacters(value) || 'Niedozwolone znaki –  /:\'"?*<>|()[]%$#@{}~^&'
            ],
        }
    },
    validations() {
        const orderGraphicFileDeliveryMethodHelper = () => {
            return helpers.withParams({ type: 'orderGraphicFileDeliveryMethodHelper' }, (value) => {
                return value === 4 ? this.hasAllProductsProductionFiles : true
            })
        }

        const transportMethodRequired = () => {
            return helpers.withParams({ type: 'transportMethodRequired' }, (value) => {
                if (!this.showTransportSection) return true

                return value !== null && value !== undefined && value !== ''
            })
        }

        const transportDistanceRequired = () => {
            return helpers.withParams({ type: 'transportDistanceRequired' }, (value) => {
                if (!this.showTransportSection) return true

                return value !== null && value !== undefined && value !== '' && value !== 0
            })
        }

        const transportCostRequired = () => {
            return helpers.withParams({ type: 'transportCostRequired' }, (value) => {
                if (!this.showTransportSection) return true

                return value !== null && value !== undefined && value !== ''
            })
        }

        return {
            order: {
                numberConfigurationTypeId: { required },
                name: {
                    required,
                    isNameValid: value => 
                        validRuleNameLength(value, 100) &&
                        !validRuleIllegalCharacters(value),
                },
                customerId: { required },
                orderGraphicFileDeliveryMethodId: {
                    isValid: orderGraphicFileDeliveryMethodHelper()
                },
                orderDeliveryMethod: {
                    methodId: { isValid: transportMethodRequired() },
                    distance: { isValid: transportDistanceRequired() },
                    cost: { isValid: transportCostRequired() }
                }
            },
        }
    },
    computed: {
        userName() {
            return this.$store.state.user.userName
        },
        numberConfigurationTypeNames() {
            const orderNumberConfigurationType = this.$store.state.user.user.profile.orderNumberConfigurationType

            return orderNumberConfigurationType.map((id) => {
                return this.numberConfigurationTypes ? this.numberConfigurationTypes.find(item => item.id == id) : id
            })
        },
        hasProducts() {
            const products = this.$store.state.orders.orderCreate.order.orderProducts

            return products ? products.length > 0 : false
        },
        hasAllProductsProductionFiles() {
            return this.order.orderProducts.every(product => {
                return product.productionFiles.length > 0
            })
        },
        hasComments() {
            return this.order.comments ? this.order.comments.length > 0 : false
        },
        hasAdviceNoteDraft() {
            if (!this.adviceNoteDraft) {
                return false
            }

            const {
                supplier,
                estimatedPaperDeliveryDate,
                paperType,
                paperWeight,
                numberOfPallets,
                attachments = []
            } = this.adviceNoteDraft

            return !!(
                supplier ||
                estimatedPaperDeliveryDate ||
                paperType ||
                paperWeight ||
                numberOfPallets ||
                attachments.length > 0
            )
        },
        // Walidacje
        orderNumberConfigurationTypeIdErrors() {
            const errors = []

            if (!this.$v.order.numberConfigurationTypeId.$dirty) return errors
            !this.$v.order.numberConfigurationTypeId.required && errors.push('Proszę wybrać typ zamówienia.')

            return errors
        },
        orderNameErrors() {
            const errors = []

            if (!this.$v.order.name.$dirty) return errors
            !this.$v.order.name.required && errors.push('Proszę podać nazwę zamówienia.')

            return errors
        },
        orderCustomerIdErrors() {
            const errors = []

            if (!this.$v.order.customerId.$dirty) return errors
            !this.$v.order.customerId.required && errors.push('Proszę wybrać klienta.')

            return errors
        },
        orderGraphicFileDeliveryMethodErrors() {
            const errors = []

            if (!this.$v.order.orderGraphicFileDeliveryMethodId.$dirty) return errors
            !this.$v.order.orderGraphicFileDeliveryMethodId.isValid && errors.push('Metoda wymaga plików produkcyjnych w produktach.')

            return errors
        },
        showTransportSection() {
            return !this.order.campaignId && !this.order.linkedToCampaign
        }
    },
    watch: {
        customerSearchQuery(value = null) {
            if (value === null || value.length < this.customerSearchQueryMinLength) return

            this.customerSearchLoading = true

            clearTimeout(customerSearchTimer)

            customerSearchTimer = setTimeout(() => {
                if (value) {
                    this.searchCustomers(value)
                } else {
                    this.customerSearchLoading = false
                    this.customersFound = []
                }
            }, 500)
        },
        'order.linkedToCampaign'(newValue) {
            if (newValue === true) {
                this.order.orderDeliveryMethod = {
                    methodId: null,
                    cost: null,
                    distance: null
                }
                this.temporarySaveOrderData()
            }
        }
    },
    created() {
        if (isEmpty(this.$store.state.orders.orderCreate.order)) {
            this.temporarySaveOrderData()
        } else {
            const storedOrder = this.$store.state.orders.orderCreate.order,
                storedDeliveryFile = this.$store.state.orders.orderCreate.deliveryFile,
                storedAdviceNoteDraft = this.$store.state.orders.orderCreate.adviceNoteDraft,
                storedPlannedSendDate = this.$store.state.orders.orderCreate.order.plannedSendDate,
                storedPlannedDeliveryDate = this.$store.state.orders.orderCreate.order.plannedDeliveryDate,
                storedReceiveMaterialDate = this.$store.state.orders.orderCreate.order.receiveMaterialDate,
                storedReceiveFileDate = this.$store.state.orders.orderCreate.order.receiveFileDate,
                storedAcceptFileDate = this.$store.state.orders.orderCreate.order.acceptFileDate

            this.order = storedOrder
            this.deliveryFile = storedDeliveryFile
            this.adviceNoteDraft = storedAdviceNoteDraft
            storedPlannedSendDate ? this.plannedSendDate = new Date(storedPlannedSendDate) : null
            storedPlannedDeliveryDate ? this.plannedDeliveryDate = new Date(storedPlannedDeliveryDate) : null
            storedReceiveMaterialDate ? this.receiveMaterialDate = storedReceiveMaterialDate.split('T')[0] : null
            storedReceiveFileDate ? this.receiveFileDate = storedReceiveFileDate.split('T')[0] : null
            storedAcceptFileDate ? this.acceptFileDate = storedAcceptFileDate.split('T')[0] : null
            this.indexedOrderProducts()

            if (this.order.customer) {
                this.order.customerId = this.order.customer.id
                this.customersFound.push(this.order.customer)
            }
        }
    },
    methods: {
        searchCustomers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie klienta')

            this.$store.dispatch('customers/getCustomers', {
                paginate: '?limit=20',
                query: `&isHidden=false&name=${query}`
            })
                .then(response => {
                    this.customersFound = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.customerSearchLoading = false
                })
        },
        changeCustomer(customer) {
            if (customer) {
                this.order.customerId = customer.id
                this.temporarySaveOrderData()
            } else {
                this.order.customerId = null
            }
        },
        // Dodaje klucz 'index' potrzebny do identyfikacji produktu na potrzeby edycji szablonu
        indexedOrderProducts() {
            this.order.orderProducts.forEach(item => {
                delete item.index
            })
            this.order.orderProducts = this.order.orderProducts.map((item, index) => ({
                index: index,
                ...item
            }))
        },
        setDefaultNumberConfigurationType() {
            if (this.numberConfigurationTypeNames.length > 0 && this.$store.state.orders.orderCreate.order.numberConfigurationTypeId === null) {
                this.order.numberConfigurationTypeId = this.numberConfigurationTypeNames[0].id
            }
        },
        setDefaultOrderGraphicFileDeliveryMethod() {
            if (this.orderGraphicFileDeliveryMethods.length > 0 && this.$store.state.orders.orderCreate.order.orderGraphicFileDeliveryMethodId === null) {
                this.order.orderGraphicFileDeliveryMethodId = this.orderGraphicFileDeliveryMethods[0].id
            }
        },
        plannedSendDateHandler(date) {
            this.plannedSendDate = date
            date ? this.order.plannedSendDate = date.toISOString() : this.order.plannedSendDate = null
            this.temporarySaveOrderData()
        },
        plannedDeliveryDateHandler(date) {
            this.plannedDeliveryDate = date
            date ? this.order.plannedDeliveryDate = date.toISOString() : this.order.plannedDeliveryDate = null
            this.temporarySaveOrderData()
        },
        receiveMaterialDateHandler(date) {
            this.receiveMaterialDate = date
            this.order.receiveMaterialDate = `${date}T00:00:00Z`
            this.receiveMaterialDatepicker = false
            this.temporarySaveOrderData()
        },
        receiveFileDateHandler(date) {
            this.receiveFileDate = date
            this.order.receiveFileDate = `${date}T00:00:00Z`
            this.receiveFileDatepicker = false
            this.temporarySaveOrderData()
        },
        acceptFileDateHandler(date) {
            this.acceptFileDate = date
            this.order.acceptFileDate = `${date}T00:00:00Z`
            this.acceptFileDatepicker = false
            this.temporarySaveOrderData()
        },
        clearReceiveMaterialDate() {
            this.receiveMaterialDate = null,
            this.order.receiveMaterialDate = null
            this.temporarySaveOrderData()
        },
        clearReceiveFileDate() {
            this.receiveFileDate = null,
            this.order.receiveFileDate = null
            this.temporarySaveOrderData()
        },
        clearAcceptFileDate() {
            this.acceptFileDate = null,
            this.order.acceptFileDate = null
            this.temporarySaveOrderData()
        },
        addProduct() {
            router.push({
                name: 'ordersInternalOrdersAddProduct',
                params: {
                    saveInStore: true
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        editProduct(index) {
            router.push({
                name: 'ordersInternalOrdersEditProduct',
                params: {
                    index: index,
                    saveInStore: true
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showConfirmRemoveProductDialog(index) {
            this.productIndex = index
            this.confirmRemoveProductDialog = true
        },
        removeProduct() {
            this.$store.dispatch('orders/removeOrderCreateProductTemplate', this.productIndex)
            this.confirmRemoveProductDialog = false
            this.indexedOrderProducts()
        },
        closeConfirmRemoveProductDialog() {
            this.productIndex = null
            this.confirmRemoveProductDialog = false
        },
        temporarySaveOrderData() {
            const payload = JSON.parse(JSON.stringify(this.order))

            payload.netPrice = this.unformatPrice(payload.netPrice)
            payload.valuation = this.unformatPrice(payload.valuation)

            this.$store.dispatch('orders/temporarySaveOrderData', {
                order: payload,
                deliveryFile: this.deliveryFile,
                adviceNoteDraft: this.adviceNoteDraft
            })
        },
        addComment() {
            this.order.comments.push({
                comment: this.newComment,
                username: this.userName
            })
            this.newComment = ''
        },
        async saveOrder() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            const payload = JSON.parse(JSON.stringify(this.order))

            payload.netPrice = this.unformatPrice(payload.netPrice)
            payload.valuation = this.unformatPrice(payload.valuation)

            this.actionLoading = true
            try {
                const response = await this.$store.dispatch('orders/createOrder', payload)
                const orderId = response.data.id

                if (this.deliveryFile) {
                    const formData = new FormData()

                    formData.append('file', this.deliveryFile)
                    formData.append('order', orderId)

                    await this.$store.dispatch('orders/createDeliveryAttachment', formData)
                }

                if (this.hasAdviceNoteDraft) {
                    await this.$store.dispatch('adviceNotes/saveAdviceNote', {
                        orderId,
                        supplier: this.adviceNoteDraft.supplier || null,
                        estimatedPaperDeliveryDate: this.adviceNoteDraft.estimatedPaperDeliveryDate || null,
                        paperType: this.adviceNoteDraft.paperType || null,
                        paperWeight: this.adviceNoteDraft.paperWeight || null,
                        numberOfPallets: this.adviceNoteDraft.numberOfPallets || null,
                        attachmentIds: this.adviceNoteDraft.attachmentIds || []
                    })
                }

                this.$store.dispatch('showNotification', {
                    type: 'success',
                    message: 'Zamówienie zostało złożone.'
                })

                router.push({ name: 'ordersInternalOrders' })
                    .catch(error => {
                        errorHandler(error)
                    })
            } catch (error) {
                errorHandler(error)
            } finally {
                this.actionLoading = false
            }
        },
        close() {
            router.push({ name: 'ordersInternalOrders' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showAdviceNoteDialog() {
            this.adviceNoteDialog = true
        },
        closeAdviceNoteDialog() {
            this.adviceNoteDialog = false
        },
        handleAdviceNoteSaved(adviceNoteData) {
            this.adviceNoteDraft = adviceNoteData
            this.temporarySaveOrderData()
        },
        showSendingParametersDialog() {
            this.sendingParametersDialog = true
        },
        closeSendingParametersDialog() {
            this.sendingParametersDialog = false
        },
        saveSendingParametersDialog(deliveryData) {
            this.order.orderDeliveryMethod = JSON.parse(JSON.stringify(deliveryData))
            this.$v.order.orderDeliveryMethod.$touch()
            this.closeSendingParametersDialog()
            this.temporarySaveOrderData()
        },
        getDeliveryMethodName(id) {
            const method = this.deliveryMethods.find(item => item.id === id)
            
            return method ? method.name : ''
        }
    }
}
</script>
